import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, Badge, Button, Input, Select } from '../components/ui';
import { api, ApiError } from '../lib/api';
import { currentUser } from '../lib/auth';
import type { ApiResponse, Campaign } from '../lib/types';

function fmtDate(s: string | null): string {
  if (!s) return '—';
  const d = new Date(s);
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

function fmtPeriod(c: { period_start?: string | null; period_finish?: string | null }): string {
  if (!c.period_start && !c.period_finish) return '—';
  return `${fmtDate(c.period_start ?? null)} – ${c.period_finish ? fmtDate(c.period_finish) : 'N/A'}`;
}

interface DeletedCampaign extends Omit<Campaign, 'items'> {
  deleted_at?: string | null;
  created_at?: string | null;
  items?: unknown[] | null;
}

/** Ordena por data de última alteração (mais recente primeiro). */
function byUpdatedDesc(a: Campaign, b: Campaign): number {
  const t = (c: Campaign) => {
    const d = new Date((c.updated_at || '') as string);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };
  return t(b) - t(a);
}

export default function CampaignsList() {
  const navigate = useNavigate();
  // Cliente (não-admin) só cria/edita/vê: lixeira, duplicate e delete são admin-only.
  const isAdmin = !!currentUser()?.isAdmin;
  const [tab, setTab] = useState<'active' | 'deleted'>('active');
  const [deleted, setDeleted] = useState<DeletedCampaign[]>([]);
  const [deletedLoaded, setDeletedLoaded] = useState(false);
  const [deletedError, setDeletedError] = useState<string | null>(null);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [method, setMethod] = useState('');
  const [status, setStatus] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get<ApiResponse<Campaign[]>>('/campaigns/list')
      .then((r) => {
        if (!alive) return;
        setCampaigns(r.data || []);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setError(e instanceof ApiError ? e.message : 'Failed to load campaigns');
      })
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const methods = useMemo(
    () => Array.from(new Set(campaigns.map((c) => c.method).filter(Boolean))) as string[],
    [campaigns]
  );

  // deletedLoaded (e não deleted.length) como cache: lixeira vazia é um
  // resultado válido, não motivo pra refetch a cada troca de aba.
  useEffect(() => {
    if (tab !== 'deleted' || deletedLoaded) return;
    setLoadingDeleted(true);
    api.get<ApiResponse<DeletedCampaign[]>>('/campaigns/deleted')
      .then((r) => { setDeleted(r.data || []); setDeletedError(null); setDeletedLoaded(true); })
      .catch((e: unknown) => setDeletedError(e instanceof ApiError ? e.message : 'Failed to load deleted campaigns'))
      .finally(() => setLoadingDeleted(false));
  }, [tab, deletedLoaded]);

  async function duplicate(cid: string) {
    try {
      await api.post(`/campaigns/duplicate?id=${cid}`, {});
      const r = await api.get<ApiResponse<Campaign[]>>('/campaigns/list');
      setCampaigns(r.data || []);
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed to duplicate'); }
  }
  async function remove(cid: string, cname: string) {
    if (!confirm(`Delete campaign "${cname}"?`)) return;
    try {
      await api.del(`/campaigns/delete?id=${cid}`);
      setCampaigns((cs) => cs.filter((c) => c.id !== cid));
      setDeletedLoaded(false); // a lixeira mudou — força refetch na próxima visita
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Failed to delete'); }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (type && (c.campaign_type || 'retargeting') !== type) return false;
      if (method && c.method !== method) return false;
      if (status && (c.status || '') !== status) return false;
      if (q) {
        const hay = `${c.id} ${c.name} ${c.client_name || ''} ${c.method || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    }).sort(byUpdatedDesc);
  }, [campaigns, search, type, method, status]);

  return (
    <Layout
      title="Campaigns"
      actions={<Button onClick={() => navigate('/campaigns/new')}>+ Add Campaign</Button>}
    >
      {/* Abas (lixeira é admin-only — o endpoint /campaigns/deleted também é) */}
      {isAdmin && (
        <div className="flex gap-1 mb-4 border-b border-border">
          {([['active', 'Active Campaigns'], ['deleted', 'Deleted Campaigns']] as const).map(([v, l]) => (
            <button key={v} onClick={() => setTab(v)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === v ? 'border-brand-600 text-brand-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {l}
            </button>
          ))}
        </div>
      )}

      {tab === 'deleted' ? (
        <Card className="overflow-hidden">
          <div className="px-4 py-3 border-b border-border text-sm text-slate-500">
            {loadingDeleted ? 'Loading…' : `${deleted.length} deleted campaign${deleted.length === 1 ? '' : 's'}`}
          </div>
          {deletedError && <div className="px-4 py-3 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">{deletedError}</div>}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                  <th className="px-4 py-3 font-medium">ID</th>
                  <th className="px-4 py-3 font-medium">Client</th>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Period</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Items</th>
                  <th className="px-4 py-3 font-medium">Created</th>
                  <th className="px-4 py-3 font-medium">Deleted At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {deleted.map((c) => (
                  <tr key={c.id} className="hover:bg-surface-muted/60">
                    <td className="px-4 py-3 font-mono text-xs text-slate-500">{c.id}</td>
                    <td className="px-4 py-3 text-slate-600">{c.client_name || '—'}</td>
                    <td className="px-4 py-3 font-medium text-ink-900">{c.name}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtPeriod(c)}</td>
                    <td className="px-4 py-3"><Badge tone={c.status === 'on' ? 'green' : 'neutral'}>{c.status}</Badge></td>
                    <td className="px-4 py-3 text-right text-slate-600">{Array.isArray(c.items) ? c.items.length : 0}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.created_at ?? null)}</td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.deleted_at ?? null)}</td>
                  </tr>
                ))}
                {!loadingDeleted && !deletedError && deleted.length === 0 && (
                  <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400">No deleted campaigns found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      ) : (
      <>
      {/* Filtros */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
        <Input placeholder="Search by name, ID or client…" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Select value={type} onChange={(e) => setType(e.target.value)}>
          <option value="">All Types</option>
          <option value="retargeting">Retargeting</option>
          <option value="branding">Branding</option>
        </Select>
        <Select value={method} onChange={(e) => setMethod(e.target.value)}>
          <option value="">All Methods</option>
          {methods.map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="on">On</option>
          <option value="off">Off</option>
        </Select>
      </div>

      <Card className="overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <span className="text-sm text-slate-500">
            {loading ? 'Loading…' : `${filtered.length} campaign${filtered.length === 1 ? '' : 's'}`}
          </span>
        </div>

        {error && (
          <div className="p-6 text-sm text-rose-700 bg-rose-50 border-b border-rose-100">
            {error}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-slate-500 bg-surface-muted">
                <th className="px-4 py-3 font-medium">Client</th>
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Type</th>
                <th className="px-4 py-3 font-medium">Method</th>
                <th className="px-4 py-3 font-medium">Period</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Updated</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-4 py-4" colSpan={8}>
                      <div className="h-4 w-full max-w-md rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
                    </td>
                  </tr>
                ))}

              {!loading &&
                filtered.map((c) => (
                  <tr key={c.id} className="hover:bg-surface-muted/60">
                    <td className="px-4 py-3 text-slate-600">{c.client_name || '—'}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-ink-900">{c.name}</div>
                      <div className="text-xs text-slate-400 font-mono">{c.id.slice(0, 18)}…</div>
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={(c.campaign_type || 'retargeting') === 'branding' ? 'blue' : 'neutral'}>
                        {c.campaign_type || 'retargeting'}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-600">{c.method || '—'}</td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{fmtPeriod(c)}</td>
                    <td className="px-4 py-3">
                      <Badge tone={c.status === 'on' ? 'green' : 'neutral'}>{c.status}</Badge>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(c.updated_at)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button variant="outline" onClick={() => navigate(`/campaigns/edit/${c.id}`)}>Edit</Button>
                        <Button variant="ghost" onClick={() => navigate(`/campaigns/view/${c.id}`)}>View</Button>
                        {isAdmin && <Button className="bg-amber-500 hover:bg-amber-600" onClick={() => duplicate(c.id)}>Duplicate</Button>}
                        {isAdmin && <Button variant="danger" onClick={() => remove(c.id, c.name)}>Delete</Button>}
                      </div>
                    </td>
                  </tr>
                ))}

              {!loading && !error && filtered.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-slate-400" colSpan={8}>
                    No campaigns found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
      </>
      )}
    </Layout>
  );
}
